import { CloseOutlined, SearchOutlined } from '@ant-design/icons';
import { Button, Input, InputRef } from 'antd';
import get from 'lodash/get';
// eslint-disable-next-line no-use-before-define
import * as React from 'react';

import {
  countActiveProjectSearchFilters,
  buildProjectSearchResults,
  emptyProjectSearchFilters,
  getProjectSearchDateBounds,
  hasProjectSearchFilters,
  ProjectSearchFilters,
  ProjectSearchResults as SharedProjectSearchResults,
  readProjectSearchVisibility,
  ProjectSearchTaskResult,
  ProjectSearchUser,
  SearchResultsSummary,
  SearchTrigger
} from '../../../../clients/shared/ProjectSearch';

import { getApplication } from '../../models/Application';
import {
  getCurrentProjectTags,
  getProjectAssignableUsers,
  getProjectUsers
} from '../../models/Project';
import { post } from '../../utils/fetch';

import { FilterBar } from './AssigneesFilter';
import styles from './index.module.css';
export { buildProjectSearchResults } from '../../../../clients/shared/ProjectSearch';
import {
  applySidebarFilterToProjectSearchFilters,
  subscribeToProjectSearchSidebarFilters
} from './sidebarFilterBridge';
import {
  setProjectSearchUnfocusedActive,
  shouldHideFeedbackStatus
} from './unfocusedActiveState';

export const PROJECT_SEARCH_DEBOUNCE_MS = 300;

export type ProjectSearchResults = SharedProjectSearchResults;

export const PROJECT_SEARCH_QUERY_PARAM = 'project_search_query';
export const PROJECT_SEARCH_ASSIGNEE_PARAM = 'project_search_assignee';
export const PROJECT_SEARCH_COMMENTS_PARAM = 'project_search_comments';
export const PROJECT_SEARCH_CREATED_AT_PARAM = 'project_search_created_at';
export const PROJECT_SEARCH_CREATED_AT_OPERATOR_PARAM =
  'project_search_created_at_operator';
export const PROJECT_SEARCH_CREATED_BY_PARAM = 'project_search_created_by';
export const PROJECT_SEARCH_DUE_DATE_PARAM = 'project_search_due_date';
export const PROJECT_SEARCH_DUE_DATE_OPERATOR_PARAM =
  'project_search_due_date_operator';
export const PROJECT_SEARCH_INVOLVES_PARAM = 'project_search_involves';
export const PROJECT_SEARCH_PAGE_PARAM = 'project_search_page';
export const PROJECT_SEARCH_SEVERITY_PARAM = 'project_search_severity';
export const PROJECT_SEARCH_STATUS_PARAM = 'project_search_status';
export const PROJECT_SEARCH_TAG_PARAM = 'project_search_tag';
export const PROJECT_SEARCH_TAG_MATCH_PARAM = 'project_search_tag_match';
export const PROJECT_SEARCH_VISIBILITY_PARAM = 'project_search_visibility';

// Written by earlier versions of the search bar; stripped so stale values from
// bookmarked URLs do not linger next to the canonical params.
const LEGACY_PROJECT_SEARCH_PARAMS = [
  'project_search_facets',
  'project_search_assignees',
  'project_search_page_match'
];

type ProjectSearchResponse = {
  tasks: ProjectSearchTaskResult[];
  search_params?: string;
  total_count: number;
};

type TaskScope = 'kanban' | 'triage' | 'archive';

type Props = {
  projectId: number;
  taskScope: TaskScope;
  onSearchLoadingChange: (isLoading: boolean) => void;
  onSearchResultsChange: (results: ProjectSearchResults | null) => void;
};

export const normalizeProjectSearchQuery = (query: string) => query.trim();

export const isProjectSearchQueryValid = (query: string) => {
  const normalizedQuery = normalizeProjectSearchQuery(query);

  return /^\d+$/.test(normalizedQuery) || normalizedQuery.length >= 3;
};

export const getProjectSearchQueryFromSearch = (search: string) =>
  normalizeProjectSearchQuery(
    new URLSearchParams(search).get(PROJECT_SEARCH_QUERY_PARAM) || ''
  );

const readTokens = (params: URLSearchParams, name: string) =>
  params
    .getAll(name)
    .map(token => token.trim())
    .filter(Boolean);

const readDateOperator = (value: string | null) =>
  value === 'before' || value === 'after' ? value : 'on';

const readCommentsFilter = (value: string | null) =>
  value === 'unread' ? value : '';

const readTagMatch = (value: string | null) =>
  value === 'all' ? value : 'any';

export const getProjectSearchFiltersFromSearch = (
  search: string
): ProjectSearchFilters => {
  const params = new URLSearchParams(search);

  return {
    assignee: readTokens(params, PROJECT_SEARCH_ASSIGNEE_PARAM),
    comments: readCommentsFilter(params.get(PROJECT_SEARCH_COMMENTS_PARAM)),
    createdAt: (params.get(PROJECT_SEARCH_CREATED_AT_PARAM) || '').trim(),
    createdAtOperator: readDateOperator(
      params.get(PROJECT_SEARCH_CREATED_AT_OPERATOR_PARAM)
    ),
    createdBy: readTokens(params, PROJECT_SEARCH_CREATED_BY_PARAM),
    dueDate: (params.get(PROJECT_SEARCH_DUE_DATE_PARAM) || '').trim(),
    dueDateOperator: readDateOperator(
      params.get(PROJECT_SEARCH_DUE_DATE_OPERATOR_PARAM)
    ),
    involves: readTokens(params, PROJECT_SEARCH_INVOLVES_PARAM),
    page: (params.get(PROJECT_SEARCH_PAGE_PARAM) || '').trim(),
    severity: readTokens(params, PROJECT_SEARCH_SEVERITY_PARAM),
    status: readTokens(params, PROJECT_SEARCH_STATUS_PARAM),
    tag: readTokens(params, PROJECT_SEARCH_TAG_PARAM),
    tagMatch: readTagMatch(params.get(PROJECT_SEARCH_TAG_MATCH_PARAM)),
    visibility: readProjectSearchVisibility(
      params.get(PROJECT_SEARCH_VISIBILITY_PARAM)
    )
  };
};

export const mergeProjectSearchParamsIntoSearch = (
  currentSearch: string,
  projectSearchParams: string
) => {
  const mergedSearchParams = new URLSearchParams(currentSearch);
  [
    PROJECT_SEARCH_QUERY_PARAM,
    PROJECT_SEARCH_ASSIGNEE_PARAM,
    PROJECT_SEARCH_COMMENTS_PARAM,
    PROJECT_SEARCH_CREATED_AT_PARAM,
    PROJECT_SEARCH_CREATED_AT_OPERATOR_PARAM,
    PROJECT_SEARCH_CREATED_BY_PARAM,
    PROJECT_SEARCH_DUE_DATE_PARAM,
    PROJECT_SEARCH_DUE_DATE_OPERATOR_PARAM,
    PROJECT_SEARCH_INVOLVES_PARAM,
    PROJECT_SEARCH_PAGE_PARAM,
    PROJECT_SEARCH_SEVERITY_PARAM,
    PROJECT_SEARCH_STATUS_PARAM,
    PROJECT_SEARCH_TAG_PARAM,
    PROJECT_SEARCH_TAG_MATCH_PARAM,
    PROJECT_SEARCH_VISIBILITY_PARAM,
    ...LEGACY_PROJECT_SEARCH_PARAMS
  ].forEach(name => mergedSearchParams.delete(name));

  if (projectSearchParams) {
    const canonicalSearchParams = new URLSearchParams(projectSearchParams);
    canonicalSearchParams.forEach((value, key) => {
      mergedSearchParams.append(key, value);
    });
  }

  const mergedSearch = mergedSearchParams.toString();
  return mergedSearch ? `?${mergedSearch}` : '';
};

export const getInitialProjectSearchQuery = () =>
  getProjectSearchQueryFromSearch(
    typeof window === 'undefined' ? '' : window.location.search
  );

export const getInitialProjectSearchFilters = () =>
  getProjectSearchFiltersFromSearch(
    typeof window === 'undefined' ? '' : window.location.search
  );

export const replaceProjectSearchParamsInUrl = (
  projectSearchParams: string
) => {
  if (typeof window === 'undefined') return;

  const nextSearch = mergeProjectSearchParamsIntoSearch(
    window.location.search,
    projectSearchParams
  );
  if (nextSearch === window.location.search) return;

  const nextUrl = `${window.location.pathname}${nextSearch}${window.location.hash}`;
  window.history.replaceState(window.history.state, '', nextUrl);
};

const ProjectSearchBar: React.FC<Props> = ({
  projectId,
  taskScope,
  onSearchLoadingChange,
  onSearchResultsChange
}) => {
  const showPublicFeedback = !!get(
    window,
    '_bugHerd.data.project_data.is_public'
  );
  const users = React.useMemo(
    () => getProjectAssignableUsers() as ProjectSearchUser[],
    []
  );
  const createdByUsers = React.useMemo(
    () => getProjectUsers() as ProjectSearchUser[],
    []
  );
  const tags = React.useMemo(
    () =>
      Object.values(getCurrentProjectTags() || {})
        .filter(tag => tag.get('active'))
        .map(tag => ({ id: `${tag.get('id')}`, label: tag.get('name') })),
    []
  );
  const application = getApplication();
  const currentUser = application?.get('currentUser')?.toJSON() || null;
  const [query, setQuery] = React.useState(getInitialProjectSearchQuery);
  const [filters, setFilters] = React.useState(getInitialProjectSearchFilters);
  const activeFilterCount = countActiveProjectSearchFilters(filters);
  const isSearchActive = !!query || hasProjectSearchFilters(filters);
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [isInputFocused, setIsInputFocused] = React.useState(false);
  const [resultCount, setResultCount] = React.useState<number | null>(null);
  const [totalCount, setTotalCount] = React.useState<number | null>(null);
  const rootRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<InputRef>(null);
  const debounceTimerRef = React.useRef<number | null>(null);
  const activeRequestRef = React.useRef<AbortController | null>(null);
  const skipNextSearchEffectRef = React.useRef(false);

  React.useEffect(() => {
    if (isExpanded && inputRef.current) inputRef.current.focus();
  }, [isExpanded]);

  React.useEffect(() => {
    setProjectSearchUnfocusedActive(
      shouldHideFeedbackStatus(isSearchActive, isInputFocused)
    );
  }, [isInputFocused, isSearchActive]);

  React.useEffect(() => () => setProjectSearchUnfocusedActive(false), []);

  React.useEffect(
    () =>
      subscribeToProjectSearchSidebarFilters(sidebarFilter => {
        setFilters(currentFilters =>
          applySidebarFilterToProjectSearchFilters(
            currentFilters,
            sidebarFilter
          )
        );
      }),
    []
  );

  const clearDebounceTimer = React.useCallback(() => {
    if (debounceTimerRef.current !== null) {
      window.clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  }, []);

  const cancelActiveRequest = React.useCallback(() => {
    if (!activeRequestRef.current) return;

    activeRequestRef.current.abort();
    activeRequestRef.current = null;
    onSearchLoadingChange(false);
  }, [onSearchLoadingChange]);

  const search = React.useCallback(
    async (searchQuery: string, searchFilters: ProjectSearchFilters) => {
      const normalizedQuery = normalizeProjectSearchQuery(searchQuery);
      if (
        !isProjectSearchQueryValid(normalizedQuery) &&
        !(normalizedQuery === '' && hasProjectSearchFilters(searchFilters))
      ) {
        return;
      }

      cancelActiveRequest();

      const abortController = new AbortController();
      const {
        dateStart: createdAtStart,
        dateEnd: createdAtEnd
      } = getProjectSearchDateBounds(searchFilters.createdAt);
      const {
        dateStart: dueDateStart,
        dateEnd: dueDateEnd
      } = getProjectSearchDateBounds(searchFilters.dueDate);
      activeRequestRef.current = abortController;
      onSearchLoadingChange(true);

      try {
        const response = (await post(
          `/projects/${projectId}/search`,
          {
            query: normalizedQuery,
            search_surface: taskScope,
            task_scope: taskScope,
            assignee: searchFilters.assignee,
            comments: searchFilters.comments,
            created_at: searchFilters.createdAt,
            created_at_end: createdAtEnd,
            created_at_operator: searchFilters.createdAtOperator,
            created_at_start: createdAtStart,
            created_by: searchFilters.createdBy,
            due_date: searchFilters.dueDate,
            due_date_end: dueDateEnd,
            due_date_operator: searchFilters.dueDateOperator,
            due_date_start: dueDateStart,
            involves: searchFilters.involves,
            page: searchFilters.page,
            severity: searchFilters.severity,
            status: searchFilters.status,
            tag: searchFilters.tag,
            tag_match: searchFilters.tagMatch,
            visibility: searchFilters.visibility
          },
          { signal: abortController.signal }
        )) as ProjectSearchResponse;

        if (activeRequestRef.current === abortController) {
          replaceProjectSearchParamsInUrl(response.search_params || '');
          setResultCount(response.tasks.length);
          setTotalCount(response.total_count);
          onSearchResultsChange(
            buildProjectSearchResults(
              normalizedQuery,
              response.tasks,
              searchFilters.page
            )
          );
        }
      } catch (error) {
        if ((error as Error).name !== 'AbortError') {
          console.error('Failed to search project tasks:', error);
          if (activeRequestRef.current === abortController) {
            onSearchResultsChange(null);
          }
        }
      } finally {
        if (activeRequestRef.current === abortController) {
          activeRequestRef.current = null;
          onSearchLoadingChange(false);
        }
      }
    },
    [
      cancelActiveRequest,
      onSearchLoadingChange,
      onSearchResultsChange,
      projectId,
      taskScope
    ]
  );

  React.useEffect(() => {
    if (skipNextSearchEffectRef.current) {
      skipNextSearchEffectRef.current = false;
      return;
    }

    const searchFilters = filters;

    clearDebounceTimer();
    cancelActiveRequest();

    if (
      !isProjectSearchQueryValid(query) &&
      !(
        normalizeProjectSearchQuery(query) === '' &&
        hasProjectSearchFilters(searchFilters)
      )
    ) {
      replaceProjectSearchParamsInUrl('');
      onSearchResultsChange(null);
      setResultCount(null);
      setTotalCount(null);
      return;
    }

    debounceTimerRef.current = window.setTimeout(() => {
      debounceTimerRef.current = null;
      search(query, searchFilters);
    }, PROJECT_SEARCH_DEBOUNCE_MS);

    return clearDebounceTimer;
  }, [
    cancelActiveRequest,
    clearDebounceTimer,
    onSearchResultsChange,
    filters,
    query,
    search
  ]);

  React.useEffect(
    () => () => {
      clearDebounceTimer();
      cancelActiveRequest();
    },
    [cancelActiveRequest, clearDebounceTimer]
  );

  const updateFilters = (nextFilters: ProjectSearchFilters) => {
    const canSearch =
      isProjectSearchQueryValid(query) ||
      (normalizeProjectSearchQuery(query) === '' &&
        hasProjectSearchFilters(nextFilters));

    clearDebounceTimer();
    skipNextSearchEffectRef.current = canSearch;
    setFilters(nextFilters);
    if (canSearch) search(query, nextFilters);
  };

  const clearSearch = React.useCallback(() => {
    clearDebounceTimer();
    cancelActiveRequest();
    setQuery('');
    setFilters(emptyProjectSearchFilters());
    setIsExpanded(false);
    setIsInputFocused(false);
    setResultCount(null);
    setTotalCount(null);
    replaceProjectSearchParamsInUrl('');
    onSearchResultsChange(null);
  }, [cancelActiveRequest, clearDebounceTimer, onSearchResultsChange]);

  const collapseSearch = React.useCallback(() => {
    setIsExpanded(false);
    setIsInputFocused(false);
  }, []);

  React.useEffect(() => {
    if (!isExpanded) return undefined;

    const handleOutsideMouseDown = (event: MouseEvent) => {
      if (
        !(event.target instanceof Node) ||
        rootRef.current?.contains(event.target)
      ) {
        return;
      }

      collapseSearch();
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (
        event.key !== 'Escape' ||
        !(event.target instanceof Node) ||
        !rootRef.current?.contains(event.target)
      ) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      clearSearch();
    };

    document.addEventListener('mousedown', handleOutsideMouseDown, true);
    document.addEventListener('keydown', handleEscape, true);
    return () => {
      document.removeEventListener('mousedown', handleOutsideMouseDown, true);
      document.removeEventListener('keydown', handleEscape, true);
    };
  }, [clearSearch, collapseSearch, isExpanded]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      clearDebounceTimer();

      if (
        isProjectSearchQueryValid(query) ||
        (normalizeProjectSearchQuery(query) === '' &&
          hasProjectSearchFilters(filters))
      ) {
        search(query, filters);
      } else {
        cancelActiveRequest();
      }
    }
  };

  return (
    <div className={styles.root} ref={rootRef}>
      {isExpanded ? (
        <div className={styles.expandedPanel}>
          <Input
            ref={inputRef}
            className={styles.expandedInput}
            prefix={<SearchOutlined />}
            suffix={
              <>
                {resultCount !== null && totalCount !== null && (
                  <span className={styles.resultCount}>
                    {resultCount} of {totalCount}
                  </span>
                )}
                {(query || hasProjectSearchFilters(filters)) && (
                  <Button
                    className={styles.clearButton}
                    icon={<CloseOutlined />}
                    onClick={clearSearch}
                    type="text"
                  >
                    Clear
                  </Button>
                )}
                <span className={styles.escapeHint}>esc</span>
              </>
            }
            value={query}
            placeholder="Search task ID, description, or comments"
            aria-label="Search tasks"
            onChange={event => setQuery(event.target.value)}
            onBlur={() => setIsInputFocused(false)}
            onFocus={() => setIsInputFocused(true)}
            onKeyDown={handleKeyDown}
          />
          <FilterBar
            createdByUsers={createdByUsers}
            currentUser={currentUser}
            filters={filters}
            onChange={updateFilters}
            showPublicFeedback={showPublicFeedback}
            tags={tags}
            users={users}
          />
        </div>
      ) : (
        <>
          <SearchTrigger
            filterCount={activeFilterCount}
            isActive={isSearchActive}
            onClick={() => setIsExpanded(true)}
            query={query}
          />
          {isSearchActive && resultCount !== null && totalCount !== null && (
            <SearchResultsSummary
              resultCount={resultCount}
              totalCount={totalCount}
              onClear={clearSearch}
            />
          )}
        </>
      )}
    </div>
  );
};

export default ProjectSearchBar;
